import {
    AlertModal,
    Box,
    Button,
    DeleteModal,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
    showToast,
} from "@bcc-ui/ui-components";
import { t } from "i18next";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import style from "./asset-folder-kebab-menu.scss";
import { CreateAssetCustomViewSubpage } from "components/asset-management-system/custom-views/CreateAssetCustomViewSubpage";
import { subpageContext } from "components/layout/ApplicationLayout";
import { enableCustomAssetViewRoutes, RouteDefinition } from "components/router/Routes";
import { AUTH_CUSTOM_ASSET_VIEW_DELETE } from "domain/authority";
import { Breakpoint } from "domain/breakpoints";
import { assetViewService } from "services/asset-management/AssetCustomViewService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { setViews } from "store/assetViews";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    route: RouteDefinition;
    onDropdownOpenChange: (isOpen: boolean) => void;
}

export const AssetFolderKebabMenu = (props: Props) => {
    const context = React.useContext(subpageContext);
    const views = useSelector((state: StoreState) => state.assetViewsReducer.views);

    const user = useSelector((state: StoreState) => state.userReducer.user);
    const abortControllerRef = React.useRef<AbortController>();
    const dispatch = useDispatch();
    const isEmptyFolder = props.route.childRoutes.length === 0;
    const hasDeleteAuthories = userSessionService.hasCustomViewPermission(
        user?.featureLicenses,
        [AUTH_CUSTOM_ASSET_VIEW_DELETE],
        "assets",
        user
    );
    const isDeleteItemVisible = hasDeleteAuthories && isEmptyFolder;
    const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = React.useState(false);

    const [isFolderDeletionFailedModalOpen, setIsFolderDeletionFailedModalOpen] = React.useState(false);
    const [isFolderNotEmptyModalOpen, setIsFolderNotEmptyModalOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<{ title: string; message: string }>({
        title: "",
        message: "",
    });
    const isMobileResolution = window.innerWidth < Breakpoint.MD;

    const openCreateAssetViewSubPage = () => {
        context?.setSubpage({
            content: (
                <CreateAssetCustomViewSubpage
                    parentUuid={props.route.viewUuid}
                    folders={(views ?? []).filter((view) => view.folder)}
                />
            ),
            isModal: false,
        });

        if (isMobileResolution) {
            context?.closeSubNavigationPanel();
        }
    };

    const deleteAssetFolder = () => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const folderUuid = props.route.viewUuid;

        if (!folderUuid) {
            return;
        }

        assetViewService
            .deleteAssetView(folderUuid, abortController)
            .then(() => {
                assetViewService
                    .fetchAssetRelatedViews()
                    .then((views) => {
                        enableCustomAssetViewRoutes(views);
                        dispatch(setViews(views));
                        showToast(
                            t("AssetManagement.deleteAssetView.folderDeleteSuccessMessage", {
                                folderName: props.route.title,
                            }),
                            "success"
                        );
                        setIsDeleteFolderModalOpen(false);
                    })

                    .catch((err) => logger.error("Failed to refresh asset views after delete:", err));
            })
            .catch((error: Error & { status: number }) => {
                if (!abortController.signal.aborted) {
                    setIsDeleteFolderModalOpen(false);

                    const isFolderNotEmpty = error.status === 400 && error.message?.includes("FOLDER_NOT_EMPTY");

                    if (isFolderNotEmpty) {
                        setIsFolderNotEmptyModalOpen(true);
                    } else {
                        setIsFolderDeletionFailedModalOpen(true);
                        setErrorMessage({
                            title: t("AssetManagement.deleteAssetView.folderNotDeletedTitle"),
                            message: t("AssetManagement.deleteAssetView.failureMessage"),
                        });
                    }
                }
            })
            .finally(() => {
                setIsDeleteFolderModalOpen(false);
            });
    };

    return (
        <>
            <DropdownMenu
                position={isMobileResolution ? "bottom-end" : "bottom-start"}
                dropdownOffset="Xxs"
                closeOnSelection
                onDropdownOpenChange={props.onDropdownOpenChange}
                flipOptions={{
                    fallbackPlacements: isMobileResolution ? ["top-end"] : ["top-start"],
                }}
            >
                <DropdownTrigger>
                    <IconButton
                        size="Sm"
                        testId={testIds.navigation.assets.assetViews.kebabMenu}
                        className={style.iconButton}
                    >
                        <Kebab size={20} color={"--app-subNavigationPanel-iconColor"} />
                    </IconButton>
                </DropdownTrigger>

                <DropdownContent className={style.dropdownContent}>
                    <DropdownMenuItem onClick={openCreateAssetViewSubPage}>
                        {t("AssetManagement.createAssetCustomView.createGroup")}
                    </DropdownMenuItem>

                    {isDeleteItemVisible && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setIsDeleteFolderModalOpen(true)} errorStyle>
                                {t("Common.delete")}
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownContent>
            </DropdownMenu>

            <DeleteModal
                title={t("AssetManagement.deleteAssetView.deleteFolderTitle")}
                isOpen={isDeleteFolderModalOpen}
                hideModal={() => setIsDeleteFolderModalOpen(false)}
                onDelete={deleteAssetFolder}
                textBundle={{
                    confirmationText: "DELETE",
                    deleteButtonText: t("AssetManagement.deleteAssetView.deleteFolder"),
                    cancelButtonText: t("Common.cancel"),
                }}
            >
                {t("AssetManagement.deleteAssetView.deleteFolderMessage", { folderName: props.route.title })}
            </DeleteModal>

            <AlertModal
                variant={"Error"}
                title={errorMessage.title}
                isOpen={isFolderDeletionFailedModalOpen}
                hideModal={() => setIsFolderDeletionFailedModalOpen(false)}
                buttons={
                    <Box gap={"Sm"}>
                        <Button variant={"Primary"} onClick={() => deleteAssetFolder()}>
                            {t("Common.deleteAgain")}
                        </Button>
                        <Button variant="Secondary" onClick={() => setIsFolderDeletionFailedModalOpen(false)}>
                            {t("Common.cancel")}
                        </Button>
                    </Box>
                }
            >
                {errorMessage.message}
            </AlertModal>

            <AlertModal
                variant={"Error"}
                isOpen={isFolderNotEmptyModalOpen}
                hideModal={() => setIsFolderNotEmptyModalOpen(false)}
                title={t("AssetManagement.deleteAssetView.folderNotDeletedTitle")}
                buttons={
                    <Button variant={"Primary"} onClick={() => setIsFolderNotEmptyModalOpen(false)}>
                        {t("Common.cancel")}
                    </Button>
                }
            >
                {t("AssetManagement.deleteAssetView.folderNotEmptyMessage")}
            </AlertModal>
        </>
    );
};
